import React, { useEffect, useRef, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { connect } from 'react-redux';

import styled from 'styled-components';

import { changeFormFieldValue, setNewForm } from 'apps/promo/actions/formAction';
import { LightFlagIcon } from 'apps/promo/components/common/fromAuth/LightFlagIcon/LightFlagIcon';
import { LightIcon } from 'apps/promo/components/common/fromAuth/LightIcon/LightIcon';
import { getField } from 'apps/promo/components/form-multi/utils';
import ModalNew from 'apps/promo/components/utils/ModalNew';
import { PHONE_COUNTRY, PHONE_NUMBER, PHONE_PREFIX } from 'apps/promo/constants/fieldsName';
import { NO_AUTH_LOGIN_REGISTRATION_FORM_NAME } from 'apps/promo/constants/formName';
import { EXCLUDE_COUNTRIES } from 'apps/promo/constants/utilsMasks';
import { logInfo } from 'apps/promo/helpers/clientLogger';
import { useCountryMobileMask } from 'apps/promo/hooks/useCountryMobileMask';
import colors from 'apps/promo/theme/colors';

import PromoSelectCountryWithCode from './PromoSelectCountryWithCode';
import PromoInputWrapper from '../PromoInputWrapper';

const FlagIconDropdown = styled.div`
    display: flex;
    padding-right: ${(props) => (props.isNonEditableCountry ? '16px' : '8px')};
    margin-right: 16px;
    border-right: 1px solid ${colors.greyRgba28};
    cursor: ${(props) => (props.isNonEditableCountry ? 'default' : 'pointer')};
`;

const NANP = new Set(['us', 'ca', 'pr']);

const PromoInputPhoneWithDropdown = (props) => {
    const ref = useRef(null);
    const modalRef = useRef(false);
    const [iso2, updateIso2] = useState('');
    const [prefix, setPrefix] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    const [fullDigits, setFullDigits] = useState('');

    const prevIso2Ref = useRef('');
    const prevDialCodeRef = useRef('');

    const { defaultMask, masks, isReady, isMaskReadyForIso2, effectiveIso2 } = useCountryMobileMask(iso2);
    const shouldInit = isReady && isMaskReadyForIso2 && !!iso2;

    useEffect(() => {
        const currentCountry = props.countries?.find(({ code_a2 }) => code_a2.toLowerCase() === props.currentCountry) || '';
        const currentIso2 = currentCountry?.code_a2?.toLowerCase();
        const currentPrefix = currentCountry.phone_prefix;
        const dialCodeDigits = currentPrefix;

        updateIso2(currentIso2);
        setPrefix(currentPrefix);

        const fields = {};
        fields[PHONE_NUMBER] = getField(PHONE_NUMBER, '', undefined, true, false, undefined, 'tel');
        fields[PHONE_PREFIX] = getField(PHONE_PREFIX, '', undefined, true, false, undefined, 'tel');
        fields[PHONE_COUNTRY] = getField(PHONE_COUNTRY, '', undefined, true, false, undefined, 'tel');
        props.setNewForm(NO_AUTH_LOGIN_REGISTRATION_FORM_NAME, fields);
        props.changeFormFieldValue(NO_AUTH_LOGIN_REGISTRATION_FORM_NAME, currentPrefix, PHONE_PREFIX);
        props.changeFormFieldValue(NO_AUTH_LOGIN_REGISTRATION_FORM_NAME, currentIso2, PHONE_COUNTRY);

        setFullDigits(dialCodeDigits);
        prevIso2Ref.current = currentIso2;
        prevDialCodeRef.current = dialCodeDigits;
    }, []);

    const handlerFocus = () => setIsFocused(true);
    const handlerBlur = () => setIsFocused(false);

    const focus = (e) => {
        if (e.currentTarget) {
            const input = e.currentTarget.firstElementChild;
            input?.focus();
        } else if (__isDev__) {
            logInfo('Focus method not support');
        }
    };

    const showCountryModal = () => {
        if (!props.isNonEditableCountry) {
            modalRef?.current?.open(
                <PromoSelectCountryWithCode
                    countries={props.countries}
                    modalRef={modalRef}
                    onCountryItemClick={onCountryItemClick}
                    searchPlaceholder={props.searchPlaceholder}
                    searchFullPlaceholder={props.searchFullPlaceholder}
                    searchModalTitle={props.searchModalTitle}
                    searchNoResult={props.searchNoResult}
                    iso2={iso2}
                />,
            );
        }
    };

    const onCountryItemClick = (code, newPrefix) => {
        const prevIso2 = prevIso2Ref.current || iso2;
        const prevDial = prevDialCodeRef.current || prefix;

        const nextIso2 = (code || '').toLowerCase();
        const nextDial = newPrefix;

        updateIso2(nextIso2);
        setPrefix(newPrefix);

        props.changeFormFieldValue(NO_AUTH_LOGIN_REGISTRATION_FORM_NAME, newPrefix, PHONE_PREFIX);
        props.changeFormFieldValue(NO_AUTH_LOGIN_REGISTRATION_FORM_NAME, nextIso2, PHONE_COUNTRY);

        const nsn = fullDigits.startsWith(prevDial) ? fullDigits.slice(prevDial.length) : '';
        const nextFull = nextDial + nsn;

        // Handle NANP internal reset (US/CA/PR)
        if (NANP.has(prevIso2) && NANP.has(nextIso2)) {
            ref.current?.updateCountry(nextIso2);
            setFullDigits('');
            setFullDigits(nextFull || nextDial);
        } else {
            // CC/CX alias
            ref.current?.updateCountry(nextIso2);
            setFullDigits(nextFull || nextDial);
        }
        prevIso2Ref.current = nextIso2;
        prevDialCodeRef.current = nextDial;

        modalRef?.current?.close();
    };

    const handlerChange = (value) => {
        const dial = prevDialCodeRef.current || prefix;
        const purePhoneNumber = (value || '').startsWith(dial) ? value.slice(dial.length) : value;
        setFullDigits(value || '');
        props.changeFormFieldValue(NO_AUTH_LOGIN_REGISTRATION_FORM_NAME, purePhoneNumber, PHONE_NUMBER);
    };

    return (
        <>
            <PromoInputWrapper
                labelText={props.labelText}
                isFilled={true}
                isFocused={isFocused}
                setIsFocused={setIsFocused}
                hasWhiteBg={props.hasWhiteBg}
                name="search"
                setFocus={focus}
                leftInputComponent={
                    <FlagIconDropdown onClick={showCountryModal} isNonEditableCountry={props.isNonEditableCountry}>
                        <LightFlagIcon name={iso2} />
                        {!props.isNonEditableCountry && <LightIcon name="chevron-down" leftIndent="4px" />}
                    </FlagIconDropdown>
                }
            >
                {shouldInit && (
                    <PhoneInput
                        ref={ref}
                        inputProps={{
                            autoComplete: 'tel',
                        }}
                        specialLabel=""
                        value={fullDigits}
                        disableDropdown
                        countryCodeEditable={false}
                        placeholder=""
                        isFocused={isFocused}
                        country={effectiveIso2}
                        onlyCountries={[effectiveIso2]}
                        defaultMask={defaultMask}
                        masks={masks}
                        excludeCountries={EXCLUDE_COUNTRIES}
                        isFilled
                        onChange={handlerChange}
                        onFocus={handlerFocus}
                        onBlur={handlerBlur}
                        enableLongNumbers={16}
                        enableTerritories={false}
                    />
                )}
            </PromoInputWrapper>
            <ModalNew ref={modalRef} />
        </>
    );
};

export default connect(null, { changeFormFieldValue, setNewForm })(PromoInputPhoneWithDropdown);
